/**
 * Plant store — mutable state for the foundry process graph.
 *
 * Wraps the static FOUNDRY_PLANT_NODES + FOUNDRY_PLANT_EDGES fixture in
 * a React Context so the copilot can add entities + connections in
 * response to user accept actions, and the process graph view reflects
 * those changes immediately.
 *
 * Mounted by the TwinRoutes parent — all /app/twin/* routes share one
 * plant.
 *
 * ── B5.1 live-API wiring ───────────────────────────────────────────────
 * When VITE_USE_LIVE_API=true the provider hydrates from
 * /v1/twin/entities + /v1/twin/connections (replacing the fixture seed on
 * first load) and writes every add/remove THROUGH to the server. Ids are
 * generated client-side BEFORE the request so the copilot / manual editor
 * keep their synchronous "return the new id" contract (docs/07 §5.5);
 * server writes are optimistic + invalidate-on-settle for reconciliation.
 * With the flag unset, no fetch fires and this behaves exactly as the
 * Phase-0 in-memory store (identical demo).
 */

import { modelLoading } from "./plant-loading";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FOUNDRY_PLANT_EDGES,
  FOUNDRY_PLANT_NODES,
  type EntityDef,
  type PlantEdge,
  type PlantNode,
} from "@foundry/foundry-plant";
import {
  isLiveApi,
  plantEdgeToCreate,
  plantNodeToCreate,
  toPlantEdge,
  toPlantNode,
  twinConnections,
  twinEntities,
  useTwinConnectionsQuery,
  useTwinEntitiesQuery,
  TWIN_CONNECTIONS_KEY,
  TWIN_ENTITIES_KEY,
  backendErrorMessage,
} from "@/lib/api";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";

export interface PlantState {
  nodes: PlantNode[];
  edges: PlantEdge[];
  /** Instantiate an entity from a template and place it on the canvas. */
  addEntity: (entity: EntityDef, instanceName: string) => string;
  /**
   * Like {@link addEntity}, but ALSO returns the in-flight server-create
   * promise so a caller can await server persistence before issuing a
   * dependent write (e.g. the CommissionWizard POSTing bindings keyed by the
   * new `entity_id`). `addEntity` fires the create fire-and-forget, which
   * races the binding POST: the binding can reach the server before the
   * entity exists and fail its FK check (issue #102). The optimistic graph
   * update is identical; only the awaitable handle differs. In stub mode
   * `persisted` resolves immediately (no server write fires).
   */
  addEntityAndPersist: (
    entity: EntityDef,
    instanceName: string,
  ) => { id: string; persisted: Promise<void> };
  /**
   * Replace an existing entity's EntityDef in place (ports / parameters /
   * declared properties), persisting via the upsert-by-id create endpoint.
   * Powers the freeform Entity builder (P2.3). Pass `instanceName` to also
   * rename the entity in the same write (P-wave3 entity edit).
   */
  updateEntity: (id: string, entity: EntityDef, instanceName?: string) => void;
  /** Connect two entity ports. */
  addEdge: (edge: Omit<PlantEdge, "id">) => string;
  removeNode: (nodeId: string) => void;
  removeEdge: (edgeId: string) => void;
  /** Reset to the canonical foundry template (client-side only). */
  reset: () => void;
  /** Number of mutations since reset — drives the dagre layout cache key. */
  version: number;
  /** The org's model is still loading (live mode, before the first read): no node list is final yet. */
  loading: boolean;
}

const PlantContext = createContext<PlantState | null>(null);

export function PlantProvider({ children }: { children: ReactNode }) {
  // Live mode: start empty + hydrate from the org-scoped server query
  // (below). Seeding the foundry fixture plant here showed the demo graph
  // in every org until the query resolved (and in empty orgs, briefly
  // flashed it). Fixtures are stub-mode only.
  const [nodes, setNodes] = useState<PlantNode[]>(
    isLiveApi ? [] : FOUNDRY_PLANT_NODES,
  );
  const [edges, setEdges] = useState<PlantEdge[]>(
    isLiveApi ? [] : FOUNDRY_PLANT_EDGES,
  );
  const [version, setVersion] = useState(0);
  // Whether the nodes hold the server's model yet: set in the same effect that sets them, so `loading` stays true
  // through the one commit where the entities query has its data but the nodes are still the empty start (#1265
  // review, N1: a page that reads the model as soon as loading turns false would read no nodes).
  const [hydrated, setHydrated] = useState(false);

  const qc = useQueryClient();
  const toast = useToast();
  const { activeOrg } = useOrg();
  const erroredRef = useRef(false);
  // Current nodes, readable synchronously from callbacks without a stale
  // closure (updateEntity needs the existing node's instanceName/position).
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // Surface a single "backend unreachable" toast, then fall through to
  // whatever local state we have (mirrors the B1.0 provider behaviour).
  const onWriteError = useCallback(
    (verb: string) => (err: unknown) => {
      if (!erroredRef.current) {
        erroredRef.current = true;
        toast.push("error", `Couldn't ${verb} on the server — change is local only.`);
      }
      // eslint-disable-next-line no-console
      console.error(`twin ${verb} failed`, err);
    },
    [toast],
  );

  const invalidateEntities = useCallback(
    () => void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY }),
    [qc],
  );
  const invalidateConnections = useCallback(
    () => void qc.invalidateQueries({ queryKey: TWIN_CONNECTIONS_KEY }),
    [qc],
  );

  // ── Hydrate from the server (live mode only) ──────────────────────────
  const entitiesQuery = useTwinEntitiesQuery();
  const connectionsQuery = useTwinConnectionsQuery();

  useEffect(() => {
    if (entitiesQuery.data) {
      setNodes(entitiesQuery.data.items.map(toPlantNode));
      setHydrated(true);
      bump();
    }
  }, [entitiesQuery.data, bump]);

  useEffect(() => {
    if (connectionsQuery.data) {
      setEdges(connectionsQuery.data.items.map(toPlantEdge));
      bump();
    }
  }, [connectionsQuery.data, bump]);

  useEffect(() => {
    // A vendor org has no Tier-2 twin (RejectViaVendor → 403); that's expected,
    // not an error — don't toast "no access" on the vendor console.
    if (activeOrg?.isVendor) return;
    const err = entitiesQuery.error || connectionsQuery.error;
    if (err && !erroredRef.current) {
      erroredRef.current = true;
      toast.push("error", backendErrorMessage(err, "local plant model"));
    }
  }, [entitiesQuery.error, connectionsQuery.error, toast, activeOrg?.isVendor]);

  const addEntityAndPersist = useCallback(
    (entity: EntityDef, instanceName: string): { id: string; persisted: Promise<void> } => {
      const id = `n_${instanceName.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${Date.now()}`;
      const node: PlantNode = {
        id,
        instanceName,
        position: { x: 0, y: 0 }, // dagre will compute
        entity,
      };
      setNodes((prev) => [...prev, node]);
      bump();
      if (!isLiveApi) {
        // Stub mode: no server write — the optimistic add is the whole story.
        return { id, persisted: Promise.resolve() };
      }
      // Live mode: share ONE create promise between the fire-and-forget
      // invalidate chain (keeps the graph eventually-consistent) and the
      // awaitable handle returned to the caller. The returned promise resolves
      // only after the server has committed the entity, so a dependent write
      // (bindings) can safely sequence after it. It never rejects — a failed
      // create surfaces the usual toast and resolves so the caller can decide
      // how to proceed (the wizard skips binding when the entity didn't land).
      let settle: () => void = () => {};
      const persisted = new Promise<void>((resolve) => {
        settle = resolve;
      });
      twinEntities
        .create(plantNodeToCreate(node))
        .catch(onWriteError("add entity"))
        .finally(() => {
          invalidateEntities();
          settle();
        });
      return { id, persisted };
    },
    [bump, invalidateEntities, onWriteError],
  );

  const addEntity = useCallback(
    (entity: EntityDef, instanceName: string): string =>
      addEntityAndPersist(entity, instanceName).id,
    [addEntityAndPersist],
  );

  const updateEntity = useCallback(
    (id: string, entity: EntityDef, instanceName?: string): void => {
      const existing = nodesRef.current.find((n) => n.id === id);
      if (!existing) return;
      const updated: PlantNode = {
        ...existing,
        entity,
        instanceName: instanceName ?? existing.instanceName,
      };
      setNodes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      bump();
      if (isLiveApi) {
        // create() is upsert-by-id (handler.go ON CONFLICT DO UPDATE), so
        // re-posting the node persists the edited def.
        twinEntities
          .create(plantNodeToCreate(updated))
          .catch(onWriteError("update entity"))
          .finally(invalidateEntities);
      }
    },
    [bump, invalidateEntities, onWriteError],
  );

  const addEdge = useCallback(
    (edge: Omit<PlantEdge, "id">): string => {
      const id = `e_${edge.source}__${edge.target}_${Date.now()}`;
      const full: PlantEdge = { id, ...edge };
      setEdges((prev) => [...prev, full]);
      bump();
      if (isLiveApi) {
        twinConnections
          .create(plantEdgeToCreate(full))
          .catch(onWriteError("add connection"))
          .finally(invalidateConnections);
      }
      return id;
    },
    [bump, invalidateConnections, onWriteError],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setEdges((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
      bump();
      if (isLiveApi) {
        // Server cascades connections touching the entity (handler.go).
        twinEntities
          .remove(nodeId)
          .catch(onWriteError("delete entity"))
          .finally(() => {
            invalidateEntities();
            invalidateConnections();
          });
      }
    },
    [bump, invalidateEntities, invalidateConnections, onWriteError],
  );

  const removeEdge = useCallback(
    (edgeId: string) => {
      setEdges((prev) => prev.filter((e) => e.id !== edgeId));
      bump();
      if (isLiveApi) {
        twinConnections
          .remove(edgeId)
          .catch(onWriteError("delete connection"))
          .finally(invalidateConnections);
      }
    },
    [bump, invalidateConnections, onWriteError],
  );

  const reset = useCallback(() => {
    // Client-side reset only — does not wipe the server plant. In live mode
    // don't repopulate fixtures (that would inject the demo into this org).
    setNodes(isLiveApi ? [] : FOUNDRY_PLANT_NODES);
    setEdges(isLiveApi ? [] : FOUNDRY_PLANT_EDGES);
    bump();
  }, [bump]);

  const value = useMemo<PlantState>(
    () => ({
      nodes,
      edges,
      addEntity,
      addEntityAndPersist,
      updateEntity,
      addEdge,
      removeNode,
      removeEdge,
      reset,
      version,
      // Loading until the first read lands in the nodes; a failed read is not loading (isSuccess false).
      loading: modelLoading({ isLoading: entitiesQuery.isLoading, isSuccess: entitiesQuery.isSuccess }, hydrated),
    }),
    [
      nodes,
      edges,
      addEntity,
      addEntityAndPersist,
      updateEntity,
      addEdge,
      removeNode,
      removeEdge,
      reset,
      version,
      entitiesQuery.isLoading,
      entitiesQuery.isSuccess,
      hydrated,
    ],
  );

  return <PlantContext.Provider value={value}>{children}</PlantContext.Provider>;
}

export function usePlant(): PlantState {
  const ctx = useContext(PlantContext);
  if (!ctx) {
    throw new Error("usePlant must be used inside <PlantProvider>");
  }
  return ctx;
}
